
import Navbar from '../Navbar';
import Footer from '../Footer';
import CipherQuiz from '@/components/CipherQuiz/CipherQuiz';
import InitiationHeader from '@/components/InitiationPage/InitiationHeader';
import ConnectWalletStep from '@/components/InitiationPage/ConnectWalletStep';
import TokenCheckStep from '@/components/InitiationPage/TokenCheckStep';
import PathSelectionStep from '@/components/InitiationPage/PathSelectionStep';
import TwitterEngagementStep from '@/components/InitiationPage/TwitterEngagementStep';

import CompletionStep from '@/components/InitiationPage/CompletionStep';
import InitiationWrapper from './InitiationWrapper';
import { useInitiationHandlers } from './InitiationHandlers';
import { useInitiationState, InitiationStep } from '@/hooks/initiation';
import { LoadingState } from '@/components/CipherQuiz/LoadingState';
import DebugPanel from './DebugPanel';
import SEO from '../SEO';
import ErrorPanel from './ErrorPanel';

const PANCAKESWAP_URL = "https://pancakeswap.finance/swap?outputCurrency=0xE4319B352cA79451C19D37c4728ba981aEEac9fA";

interface InitiationContentProps {
  address: `0x${string}` | undefined;
  isConnected: boolean;
  supabaseReady: boolean | null;
  connectionError: string | null;
  showDebug: boolean;
  mobileDetected: boolean;
}

const InitiationContent = ({ 
  address, 
  supabaseReady, 
  connectionError,
  showDebug,
  mobileDetected
}: InitiationContentProps) => {
  const {
    currentStep,
    hasToken,
    isCheckingToken,
    completionRank,
    completionBrocode,
    tableSetupDone,
    handleDisconnectWallet,
    handleRecheckWallet,
    handleProceedToPathSelection,
    handleSelectCipher,
    
    handleSelectTwitter,
    handleQuizCompletion,
    
    handleTwitterCompletion,
    handleBackToPathSelection,
  } = useInitiationState();

  const {
    handleConnectWallet
  } = useInitiationHandlers(mobileDetected);

  // If Supabase connection is being checked
  if (supabaseReady === null) {
    return (
      <div className="min-h-screen bg-navy flex flex-col">
        <Navbar />
        <div className={`flex-grow ${mobileDetected ? 'pt-16 pb-28' : 'py-28'} px-4 flex flex-col items-center justify-center`}>
          <LoadingState message="Initializing connection..." />
        </div>
        <Footer />
      </div>
    );
  }

  // If there's a connection error
  if (!supabaseReady && connectionError) {
    return (
      <div className="min-h-screen bg-navy flex flex-col">
        <Navbar />
        <div className={`flex-grow ${mobileDetected ? 'pt-16 pb-28' : 'py-28'} px-4 flex flex-col items-center justify-center`}>
          <ErrorPanel errorMessage={connectionError} />
        </div>
        <Footer />
      </div>
    );
  }

  const timestamp = new Date().getTime(); // Add timestamp for cache busting

  return (
    <div className="min-h-screen bg-navy flex flex-col">
      <SEO 
        title="Bromentum Initiation | Take The Cipher"
        description="Take the Cipher - Enter the swarm and become part of the cultural flippening."
        url="https://bromentum.xyz/initiation"
        image={`/assets/bromentum logo.jpg?v=5&t=${timestamp}`}
        siteName="Bromentum"
      />
      
      <Navbar />
      
      <div className={`flex-grow ${mobileDetected ? 'pt-16 pb-28' : 'py-28'} px-4 flex flex-col items-center justify-center`}>
        <InitiationWrapper mobileDetected={mobileDetected} currentStep={currentStep}>
          <InitiationHeader />

          {mobileDetected && (
            <div className="mb-4 px-4 py-3 bg-tan text-navy-dark rounded-md text-sm">
              <p className="font-bold">📱 Mobile device detected</p>
              <p>For the best experience, ensure your wallet app is properly connected to Binance Smart Chain (BSC).</p>
            </div>
          )}

          <div className="bg-navy-light p-8 rounded-lg border border-tan-dark animate-fade-in">
            {/* Debug wallet and state info - only shown when debug mode is active */}
            {showDebug && <DebugPanel 
              address={address} 
              currentStep={currentStep} 
              supabaseReady={supabaseReady} 
              hasToken={hasToken} 
              mobileDetected={mobileDetected} 
            />}

            {currentStep === InitiationStep.CONNECT_WALLET && (
              <ConnectWalletStep onConnectWallet={handleConnectWallet} />
            )}

            {currentStep === InitiationStep.TOKEN_CHECK && (
              <TokenCheckStep 
                isCheckingToken={isCheckingToken}
                hasToken={hasToken}
                onProceedAnyway={handleProceedToPathSelection}
                onRecheckWallet={handleRecheckWallet}
                onTakeCipher={handleProceedToPathSelection}
                onDisconnect={handleDisconnectWallet}
                pancakeswapUrl={PANCAKESWAP_URL}
              />
            )}

            {currentStep === InitiationStep.PATH_SELECTION && (
              <PathSelectionStep 
                onSelectCipher={handleSelectCipher}
                onSelectTwitter={handleSelectTwitter}
                hasToken={hasToken}
              />
            )}

            {currentStep === InitiationStep.TAKE_QUIZ && address && (
              <CipherQuiz 
                hasToken={hasToken}
                walletAddress={address}
                onComplete={handleQuizCompletion}
                tableReady={tableSetupDone && supabaseReady}
              />
            )}

            {currentStep === InitiationStep.TWITTER_ENGAGEMENT && address && (
              <TwitterEngagementStep
                onComplete={() => handleTwitterCompletion('SOCIAL_BRO', `SOCIAL-${Date.now()}`)}
                walletAddress={address}
              />
            )}

            {currentStep === InitiationStep.COMPLETION && address && (
              <CompletionStep 
                completionRank={completionRank}
                broData={{ 
                  id: crypto.randomUUID(),
                  wallet_address: address,
                  brocode: completionBrocode, 
                  brocode_prefix: completionBrocode.split('-')[0] || '',
                  rank: completionRank,
                  bro_score: 0, 
                  initiated: true,
                  is_brometheus: completionRank === 'BROMETHEUS',
                  xp_cost: 0,
                  created_at: new Date().toISOString()
                }}
                pancakeswapUrl={PANCAKESWAP_URL}
                mobileDetected={mobileDetected}
                onBroDataUpdate={(newData) => {
                  // Handle broData updates if needed
                  console.log('BroData updated:', newData);
                }}
              />
            )}
          </div>
        </InitiationWrapper>
      </div>
      
      <Footer />
    </div>
  );
};

export default InitiationContent;
